/**
 * PaymentBrick — embedded Mercado Pago Bricks checkout (M215 + M240).
 *
 * IMPORTANT: the Brick MUST mount exactly once per intentId. Earlier versions
 * of this component listed the callback props (onApproved/onPending/onRejected)
 * in the useEffect deps; because those are inline arrows from the parent, every
 * unrelated parent re-render (cart polling, etc.) re-ran the effect and
 * remounted the Brick. That triggered the MP antifraud scripts (armor.*) to
 * inject duplicate iframes and eventually forced a window reload. Callbacks
 * now live in a ref that is updated on every render — the Brick reads the
 * latest version on submit, but does NOT remount when callbacks change.
 *
 * M240 — Error handling for failed card attempts:
 * Previously when the backend returned {status: "rejected", ...} we still
 * RESOLVED the Brick's onSubmit promise, so the Brick thought the submission
 * had succeeded and stayed locked in "submitted" state with no retry. Now we
 * categorize the failure (validation / rejection / generic), surface a clear
 * Portuguese message below the Brick, and THROW so the Brick re-enables the
 * "Pagar" button and the customer can fix the data and try again without a
 * reload. The 15-minute reservation lock is NOT touched on failure — only
 * the approved branch in `_apply_post_payment_side_effects` clears it.
 */
import { useEffect, useRef, useState } from "react";
import { loadMercadoPago } from "@mercadopago/sdk-js";
import { api } from "../lib/api";
import { useMaxInstallments } from "../lib/installments";

// --- Error categorization -------------------------------------------------
// Buckets follow Mercado Pago's `status_detail` taxonomy. Validation-bucket
// failures are user-correctable on the spot (typed wrong CVV / number / date)
// — we don't offer the consultora CTA because nothing is broken on our side.
// Rejection-bucket failures are bank-side (limit, fraud, disabled) — we offer
// the consultora CTA so the customer has a path forward if their cards fail
// repeatedly.
const VALIDATION_DETAILS = new Set([
  "cc_rejected_bad_filled_card_number",
  "cc_rejected_bad_filled_date",
  "cc_rejected_bad_filled_security_code",
  "cc_rejected_bad_filled_other",
  "cc_rejected_invalid_installments",
]);

const REJECTION_DETAILS = new Set([
  "cc_rejected_other_reason",
  "cc_rejected_insufficient_amount",
  "cc_rejected_call_for_authorize",
  "cc_rejected_high_risk",
  "cc_rejected_card_disabled",
  "cc_rejected_duplicated_payment",
  "cc_rejected_max_attempts",
  "cc_rejected_card_error",
]);

function categorizeRejection(statusDetail) {
  if (statusDetail && VALIDATION_DETAILS.has(statusDetail)) {
    return {
      bucket: "validation",
      message: "Verifique os dados do cartão e tente novamente.",
    };
  }
  if (statusDetail && REJECTION_DETAILS.has(statusDetail)) {
    return {
      bucket: "rejection",
      message: "Pagamento não aprovado. Verifique com seu banco ou tente outro cartão.",
    };
  }
  return {
    bucket: "generic",
    message: "Não conseguimos processar seu pagamento. Revise suas informações ou entre em contato com a gente.",
  };
}

// Maps known Brick-side validation events to friendly copy. The Bricks SDK
// fires `onError` with a `cause` array for client-side validation (e.g. user
// hit "Pagar" with an empty CVV field). The exact codes are documented here:
// https://www.mercadopago.com.br/developers/pt/docs/checkout-bricks/payment-brick/additional-customization/notifications-on-error
function categorizeBrickError(err) {
  const cause = err?.cause?.[0]?.code || err?.cause?.[0] || "";
  const code = (cause && String(cause)) || String(err?.code || "") || "";
  // Anything that smells like a field-level validation issue stays in the
  // "validation" bucket so we don't scare the customer with a generic error.
  if (/invalid|required|expired|format|cvv|security|number|date/i.test(code)) {
    return {
      bucket: "validation",
      message: "Verifique os dados do cartão e tente novamente.",
    };
  }
  return {
    bucket: "generic",
    message: "Não conseguimos processar seu pagamento. Revise suas informações ou entre em contato com a gente.",
  };
}

export default function PaymentBrick({
  intentId,
  amount,
  publicKey,
  payerEmail,
  onApproved,
  onPending,
  onRejected,
  onRequestHelp,
  restrictTo,   // "credit_card" | "bank_transfer" | null (defaults to both)
}) {
  const containerRef = useRef(null);
  const brickControllerRef = useRef(null);
  const maxInstallments = useMaxInstallments();
  // `error` shape: { message: string, bucket: "validation"|"rejection"|"generic"|"" }
  const [error, setError] = useState({ message: "", bucket: "" });

  // Keep latest callbacks accessible to the Brick `onSubmit` handler without
  // triggering re-mounts when the parent re-renders with new function refs.
  const callbacksRef = useRef({ onApproved, onPending, onRejected, onRequestHelp });
  useEffect(() => {
    callbacksRef.current = { onApproved, onPending, onRejected, onRequestHelp };
  }, [onApproved, onPending, onRejected, onRequestHelp]);

  useEffect(() => {
    if (!intentId || !publicKey) return undefined;
    let cancelled = false;

    (async () => {
      try {
        await loadMercadoPago();
        if (cancelled) return;

        // Defensive cleanup: if a prior instance survived a fast-refresh, drop it.
        if (brickControllerRef.current) {
          try { brickControllerRef.current.unmount(); } catch (e) { console.warn("PaymentBrick: prior controller unmount failed", e); }
          brickControllerRef.current = null;
        }
        const node = document.getElementById("prize-payment-brick");
        if (node) node.innerHTML = "";

        const mp = new window.MercadoPago(publicKey, { locale: "pt-BR" });
        const bricks = mp.bricks();

        const controller = await bricks.create("payment", "prize-payment-brick", {
          initialization: {
            amount: Number(amount),
            payer: {
              email: payerEmail || "",
              entityType: "individual",
            },
          },
          customization: {
            // MP's Brick treats unknown values (like "off") as fallback to
            // the default. To truly hide a payment method we must OMIT the
            // key entirely — hence the conditional spread instead of a
            // "creditCard: 'off'" literal.
            paymentMethods: {
              ...(restrictTo === "bank_transfer" ? {} : { creditCard: "all" }),
              ...(restrictTo === "credit_card" ? {} : { bankTransfer: "all" }),
              // Single source of truth: db.settings.installments. Editable from
              // /admin/configuracoes without any code change.
              maxInstallments,
            },
            visual: {
              style: {
                theme: "default",
                customVariables: {
                  baseColor: "#B8A88A",
                  baseColorFirstVariant: "#A89376",
                  baseColorSecondVariant: "#F5EFE8",
                  formBackgroundColor: "#FFFFFF",
                  buttonTextColor: "#FFFFFF",
                  textPrimaryColor: "#1A1A1A",
                  textSecondaryColor: "#6B6B6B",
                  successColor: "#3E6A1E",
                  errorColor: "#A8503B",
                  outlinePrimaryColor: "#B8A88A",
                  outlineSecondaryColor: "#E8E4DF",
                  borderRadiusSmall: "0px",
                  borderRadiusMedium: "0px",
                  borderRadiusLarge: "0px",
                  borderRadiusFull: "0px",
                  fontSizeExtraSmall: "11px",
                  fontSizeSmall: "13px",
                  fontSizeMedium: "15px",
                  fontSizeLarge: "17px",
                  fontWeightNormal: 400,
                  fontWeightSemiBold: 500,
                },
              },
            },
          },
          callbacks: {
            onReady: () => { /* Brick rendered — keep this no-op, do not setState */ },
            onError: (err) => {
              // Bricks-side validation / loading errors. Never reload — surface
              // a friendly message and keep the form editable. The Brick
              // re-enables its own internal state automatically.
              console.error("Bricks onError:", err);
              const cat = categorizeBrickError(err);
              setError(cat);
            },
            onSubmit: async (data) => {
              // Clear any previous error so the customer doesn't see stale copy
              // while their new attempt is in flight.
              setError({ message: "", bucket: "" });
              const fd = data?.formData || {};
              const body = {
                intent_id: intentId,
                payment_method_id: fd.payment_method_id,
                installments: fd.installments || 1,
                token: fd.token || null,
                issuer_id: fd.issuer_id || null,
                payer: fd.payer || {},
              };
              let response;
              try {
                response = await api.post("/mp/checkout/process", body);
              } catch (e) {
                const httpStatus = e?.response?.status;
                const detail = e?.response?.data?.detail;
                let message;
                if (httpStatus === 409 && (detail?.code === "already_paid" || /já foi processado/i.test(detail?.message || ""))) {
                  message = "Este pedido já foi processado em outra tentativa. Volte ao carrinho para iniciar um novo pedido.";
                } else if (typeof detail === "string") {
                  message = detail;
                } else if (detail?.message) {
                  message = detail.message;
                } else {
                  message = "Não conseguimos processar seu pagamento. Revise suas informações ou entre em contato com a gente.";
                }
                setError({ message, bucket: "generic" });
                // Re-throw so the Brick stays on the same screen with the
                // "Pagar" button re-enabled. NEVER call window.location.reload().
                throw e;
              }

              const status = response.data?.status;
              const statusDetail = response.data?.status_detail;
              const cbs = callbacksRef.current || {};

              if (status === "approved") {
                cbs.onApproved?.(response.data);
                return response.data;
              }
              if (status === "pending" || status === "in_process") {
                cbs.onPending?.(response.data);
                return response.data;
              }
              // rejected / unknown → surface error, fire callback, and REJECT
              // the Brick's submit promise so it re-enables the form and the
              // customer can correct the data and retry without a reload.
              // The 15-min reservation lock is preserved server-side (the
              // "approved" branch is the only path that clears reservations).
              const cat = categorizeRejection(statusDetail);
              setError(cat);
              cbs.onRejected?.(response.data);
              const err = new Error(cat.message);
              err.status_detail = statusDetail;
              throw err;
            },
          },
        });

        if (cancelled) {
          try { controller.unmount(); } catch (e) { console.warn("PaymentBrick: post-cancel unmount failed", e); }
          return;
        }
        brickControllerRef.current = controller;
      } catch (e) {
        console.error("PaymentBrick init failed:", e);
        setError({
          message: "Não foi possível carregar o checkout do Mercado Pago. Recarregue a página em alguns instantes.",
          bucket: "generic",
        });
      }
    })();

    return () => {
      cancelled = true;
      if (brickControllerRef.current) {
        try { brickControllerRef.current.unmount(); } catch (e) { console.warn("PaymentBrick: cleanup unmount failed", e); }
        brickControllerRef.current = null;
      }
    };
    // Mount the Brick exactly once per intentId — callbacks are read via ref.
  }, [intentId, amount, publicKey, payerEmail, maxInstallments, restrictTo]);

  return (
    <div data-testid="payment-brick">
      <div id="prize-payment-brick" ref={containerRef} />

      {/* Inline error panel — appears immediately BELOW the Brick after any
          failed validation or rejection. The Brick form remains editable, no
          reload happens, and the reservation lock keeps ticking. */}
      {error.message && (
        <div
          className="mt-4 p-4 border border-[#E5C8BE] bg-[#FBF2EE]"
          data-testid="payment-brick-error"
          data-bucket={error.bucket}
          role="alert"
          aria-live="polite"
        >
          <p className="text-sm text-[#A8503B] leading-relaxed">{error.message}</p>
          <div className="mt-3">
            <button
              type="button"
              onClick={() => callbacksRef.current?.onRequestHelp?.()}
              className="text-xs text-[#8C7B5E] hover:text-[#1A1A1A] underline underline-offset-4"
              data-testid="payment-brick-help-cta"
            >
              Precisa de ajuda? Fale com nossa equipe →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
